import { Button } from '@components/buttons';
import { IcoCalendar, IcoCheck, IcoLock, IcoX } from '@components/icons';
import { useCurrentUser } from 'client/lib/auth';
import { PriceDisplay } from '@components/price-display';
import type { PaneProps } from '../../types';
import { ComponentChildren } from 'preact';
import { StatusPill } from './status-pill';

function FeatureCard({
  icon,
  title,
  description,
  bg,
  border,
  iconBg,
}: {
  icon: ComponentChildren;
  title: string;
  description: string;
  bg: string;
  border?: string;
  iconBg?: string;
}) {
  return (
    <div
      class={`flex flex-col items-center gap-4 px-4 py-6 ${bg} rounded-xl border z-10 ${border}`}
    >
      {iconBg && (
        <div
          class={`w-10 h-10 ${iconBg} rounded-xl flex items-center justify-center flex-shrink-0`}
        >
          {icon}
        </div>
      )}
      {!iconBg && icon}
      <div class="flex-1 text-center">
        <div class="text-base font-semibold text-gray-900 mb-1">{title}</div>
        <div class="text-sm text-gray-600">{description}</div>
      </div>
    </div>
  );
}

export function InfoStep({ onNext, price, coupon }: PaneProps) {
  const currentUser = useCurrentUser();

  return (
    <div class="flex flex-col h-full py-4">
      <div class="absolute top-4 right-4">
        <Button
          class="text-gray-600 hover:text-gray-800 p-2 rounded-full transition-colors"
          href="/calendar/page-builder"
        >
          <IcoX class="w-5 h-5" />
        </Button>
      </div>
      {/* Content area */}
      <div class="flex-1 flex flex-col gap-6">
        {/* Header with gradient accent */}
        <section class="text-center px-16 pt-8">
          <StatusPill text="Ready to publish" theme="gradient" animated className="mb-4" />
          <h1 class="text-3xl font-bold bg-gradient-to-r from-gray-900 to-gray-600 bg-clip-text text-transparent mb-3">
            You're Ready to Launch. Let's Take It Live.
          </h1>
          <p class="text-lg text-gray-600 leading-relaxed">
            Your new page is ready. Let's publish it, turn on your calendar, and give you early
            access to everything coming next.
          </p>
          <div class="mt-6 aspect-w-16 aspect-h-9 mx-32 rounded-lg overflow-hidden border">
            <iframe
              src="https://player.vimeo.com/video/1115314421?h=ab2a497f29&amp;badge=0&amp;autopause=0&amp;player_id=0&amp;app_id=58479&amp;autoplay=1&muted=0"
              allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media; web-share"
              style="width:100%;height:100%;"
              title="CoachMira Registration Flow"
            ></iframe>
          </div>
        </section>

        {/* Transformation Timeline */}
        <section class="p-8 border">
          <div class="flex flex-col items-center gap-8 p-8 border rounded-xl">
            <h3 class="text-lg font-bold">Your transformation timeline:</h3>
            <div class="relative grid grid-cols-1 md:grid-cols-3 gap-6 w-full">
              <div class="hidden md:block absolute top-1/2 left-0 right-0 h-0.5 bg-gray-200 pointer-events-none" />
              <FeatureCard
                icon={<IcoCheck class="w-5 h-5 text-white" />}
                title="Page Goes Live"
                description="Professional hosting with instant publication"
                bg="bg-gradient-to-r from-blue-50 to-indigo-50"
                border="border-blue-100/50"
                iconBg="bg-gradient-to-r from-blue-500 to-indigo-500"
              />
              <FeatureCard
                icon={<IcoCalendar class="w-5 h-5 text-white" />}
                title="Calendar Active"
                description="Automated booking system ready for appointments"
                bg="bg-gradient-to-r from-emerald-50 to-green-50"
                border="border-emerald-100/50"
                iconBg="bg-gradient-to-r from-emerald-500 to-green-500"
              />
              <FeatureCard
                icon={<IcoLock class="w-5 h-5 text-white" />}
                title="Rate Locked"
                description="Founder pricing protected permanently"
                bg="bg-gradient-to-r from-purple-50 to-pink-50"
                border="border-purple-100/50"
                iconBg="bg-gradient-to-r from-purple-500 to-pink-500"
              />
            </div>
          </div>
        </section>

        <section class="flex flex-col items-center gap-8 py-8 px-12">
          <h3 class="text-lg font-bold">Our Vision: The Future of Coaching</h3>
          <p class="text-lg text-gray-600 text-center leading-relaxed px-8">
            CoachMira isn't just another website builder. We're creating the first truly intelligent
            coaching platform that grows with you and your practice.
          </p>
          <div class="relative grid grid-cols-1 md:grid-cols-2 gap-6 w-full">
            <FeatureCard
              icon={<span class="text-3xl">🎯</span>}
              title="Effortless Growth"
              description="Your website becomes a living, breathing extension of your coaching practice—automatically optimizing to attract and convert your ideal clients."
              bg="bg-gray-100"
            />
            <FeatureCard
              icon={<span class="text-3xl">🤖</span>}
              title="AI-Powered Intelligence"
              description="Every interaction teaches the system more about your unique approach, allowing it to better represent you and serve your clients."
              bg="bg-gray-100"
            />
            <FeatureCard
              icon={<span class="text-3xl">🌱</span>}
              title="Seamless Integration"
              description="One platform that handles everything—from first impression to final session note—so you can focus entirely on what you do best: coaching."
              bg="bg-gray-100"
            />
          </div>
        </section>

        <section class="w-full px-12 pb-8">
          <div class="text-center text-lg italic text-blue-700 py-8 px-12 bg-gradient-to-r from-blue-50 via-indigo-50 to-blue-50 rounded-2xl border border-blue-100">
            "We're building this future one coach at a time, starting with the foundation you need
            today."
          </div>
        </section>
      </div>
      <section class="flex py-8 px-12 border-t">
        <div class="flex grow flex-col items-center gap-4 p-8 border rounded-xl">
          <StatusPill className="mb-4" text="Founding Member" theme="blue" animated />
          {price && <PriceDisplay price={price} coupon={coupon} />}
          <p class="text-base text-gray-600">Lock in early access pricing</p>
          <div class="flex flex-col text-sm text-gray-600 text-center">
            <span>Your price stays locked until the full platform is delivered.</span>
            <span>Cancel anytime • 30-day refund guarantee</span>
          </div>
        </div>
      </section>

      <footer class="flex justify-center bg-white border-t border-gray-100 px-12 py-6 mt-6">
        <Button
          onClick={onNext}
          class="bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white py-4 px-12 rounded-xl font-semibold text-lg shadow-lg shadow-indigo-500/25 transition-all duration-200 hover:shadow-xl hover:shadow-indigo-500/30"
        >
          {currentUser ? 'Continue to Payment' : 'Get Started'}
        </Button>
      </footer>
    </div>
  );
}
